import { positionCategoryToLabel } from "@/lib/constants";
import {
  ExploreImage,
  ExploreImageFocus,
  ExploreImageIndustry,
  PositionCategory,
} from "@/types";

export const exploreImageIndustryToLabel: Record<ExploreImageIndustry, string> =
  {
    [ExploreImageIndustry.AEROSPACE_AND_DEFENSE]: "Aerospace & Defense",
    [ExploreImageIndustry.AUTOMOTIVE_AND_EV]: "Automotive & EV",
    [ExploreImageIndustry.SEMICONDUCTORS_AND_ELECTRONICS]:
      "Semiconductors & Electronics",
    [ExploreImageIndustry.ROBOTICS_AND_INDUSTRIAL_MACHINERY]:
      "Robotics & Industrial Machinery",
    [ExploreImageIndustry.FOOD_BEVERAGE_AND_CONSUMER_GOODS]:
      "Food, Beverage & Consumer Goods",
    [ExploreImageIndustry.MEDICAL]: "Medical",
    [ExploreImageIndustry.ENERGY_AND_NUCLEAR]: "Energy & Nuclear",
    [ExploreImageIndustry.CONSTRUCTION_AND_DATA_CENTERS]:
      "Construction & Data Centers",
    [ExploreImageIndustry.LOGISTICS_AND_DISTRIBUTION]:
      "Logistics & Distribution",
    [ExploreImageIndustry.BUILDING_PRODUCTS]: "Building Products",
    [ExploreImageIndustry.MACHINE_AND_FABRICATION_SHOPS]:
      "Machine & Fabrication Shops",
    [ExploreImageIndustry.STEEL_AND_METAL]: "Steel & Metal",
    [ExploreImageIndustry.CHEMICALS_AND_MATERIALS]: "Chemicals & Materials",
    [ExploreImageIndustry.SHIPBUILDING]: "Shipbuilding",
    [ExploreImageIndustry.MARINE]: "Marine",
  };

// Match guac's keyword limits.
export const EXPLORE_IMAGE_MAX_KEYWORDS = 20;
export const EXPLORE_IMAGE_KEYWORD_MAX_LENGTH = 40;

/** The one-line credit the app shows on the photo. */
export const formatExploreImageCredit = ({
  credit,
  license,
}: {
  credit: string;
  license: string;
}) => `Photo: ${credit} · ${license}`;

// CSS `object-position` for an explore image's focus. The worker app passes
// the same percentages to expo-image's `contentPosition`, so the admin
// previews crop exactly like the app.
export const toObjectPosition = ({ x, y }: ExploreImageFocus) =>
  `${x.toString()}% ${y.toString()}%`;

// A full-width feed card on a 3x phone needs about this many pixels across.
// Guac accepts narrower images (down to 800px), but they can look soft there.
export const LOW_RES_EXPLORE_IMAGE_WIDTH = 1200;

// Matches guac's upload minimum; it rejects anything narrower.
export const EXPLORE_IMAGE_MIN_WIDTH = 800;

// Matches guac's caption limit.
export const EXPLORE_IMAGE_CAPTION_MAX_LENGTH = 200;

export const isLowResExploreImage = (image: ExploreImage) =>
  image.width < LOW_RES_EXPLORE_IMAGE_WIDTH;

export const isPortraitExploreImage = (image: ExploreImage) =>
  image.height > image.width;

export const formatExploreImageSize = (bytes: number) =>
  `${Math.round(bytes / 1024).toString()} KB`;

// Overrides the dialog's centred card for the lightbox and the upload view:
// full-bleed, no padding or gap, and a block box so the base `grid gap-4`
// adds no height.
export const EXPLORE_IMAGE_FULL_SCREEN_DIALOG_CLASS_NAME =
  "left-0 top-0 block h-dvh w-screen max-w-none translate-x-0 translate-y-0 gap-0 overflow-y-auto border-0 bg-[#FAFAFA] p-0 text-[#212327] data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0 data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0 dark:bg-[#0E0F11] dark:text-[#FAFAFA] sm:rounded-none lg:overflow-hidden";

// Fixed chrome heights, so a stage image can be sized to exactly the space
// left: viewport − top bar (h-14) − stage footer (h-12) − stage padding (py-6).
export const EXPLORE_IMAGE_STAGE_MAX_HEIGHT = "max-h-[calc(100dvh-152px)]";

// The worker app's chamfer: top-left and bottom-right corners cut at 45°, as
// on its ChamferChip and map cards.
export const chamferClipPath = (size: number) => {
  const cut = `${size.toString()}px`;
  return `polygon(${cut} 0, 100% 0, 100% calc(100% - ${cut}), calc(100% - ${cut}) 100%, 0 100%, 0 ${cut})`;
};

/**
 * Global gallery search. Every whitespace-separated term must match the
 * caption, a keyword, one of the image's category or industry labels, or the
 * start of its id.
 */
export const matchesExploreImageSearch = (
  image: ExploreImage,
  search: string,
) => {
  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;

  const caption = (image.caption ?? "").toLowerCase();
  const labels = [
    ...image.positionCategories.map(
      (category) => positionCategoryToLabel[category],
    ),
    ...image.industries.map(
      (industry) => exploreImageIndustryToLabel[industry],
    ),
  ].map((label) => label.toLowerCase());
  const id = image.id.toLowerCase();

  return terms.every(
    (term) =>
      caption.includes(term) ||
      image.keywords.some((keyword) => keyword.includes(term)) ||
      labels.some((label) => label.includes(term)) ||
      id.startsWith(term),
  );
};

export interface ExploreImageFilters {
  search: string;
  category: PositionCategory | null;
  industry: ExploreImageIndustry | null;
  lowRes: boolean;
  portrait: boolean;
  noCaption: boolean;
  noKeywords: boolean;
}

export const filterExploreImages = (
  images: ExploreImage[],
  {
    search,
    category,
    industry,
    lowRes,
    portrait,
    noCaption,
    noKeywords,
  }: ExploreImageFilters,
) =>
  images.filter(
    (image) =>
      (!category || image.positionCategories.includes(category)) &&
      (!industry || image.industries.includes(industry)) &&
      (!lowRes || isLowResExploreImage(image)) &&
      (!portrait || isPortraitExploreImage(image)) &&
      (!noCaption || !image.caption) &&
      (!noKeywords || !image.keywords.length) &&
      matchesExploreImageSearch(image, search),
  );
